import { LocalStorageKey } from '@/common/utils/localStorage'
import { fbAuth } from '@/modules/authentication'
import { getImpersonatedUserId } from '@/modules/authentication/impersonation'

/** Bucket for a session with no identity. Never written, so it never remembers anything. */
const NO_IDENTITY = 'anonymous'

export interface RegionStorage {
  key: string
  hasIdentity: boolean
}

/**
 * Where to keep the last region a user was in, keyed by the identity that was browsing.
 *
 * It has to be per identity rather than per browser: the value outlives a session, so a
 * single browser-wide key hands one account's last region to the next account to log in
 * here, sending them to a region they may have no access to. Scoping it keeps each
 * account's own history working across sign-out, which clearing the value would not.
 *
 * An impersonated session browses as the impersonated user and so gets its own entry,
 * which also keeps the admin's own last region from following them into it, or back out.
 *
 * Both ids are read synchronously, and deliberately not through `useAuth`: that hook
 * keeps its state in a per-component reducer seeded with `{ loading: true }` and only
 * receives the user from an effect, so every freshly mounted consumer would see no
 * identity on its first render and read the anonymous bucket - long enough to redirect
 * away before the real key arrived. `LoginGatekeeper` has already waited for auth before
 * anything that calls this mounts, so `fbAuth.currentUser` is populated.
 */
export const getRegionStorageKey = (): RegionStorage => {
  const impersonatedUserId = getImpersonatedUserId()
  const uid = fbAuth.currentUser?.uid
  // A Populus user id and a Firebase uid are different id spaces, so each is prefixed
  // rather than sharing one namespace - which also puts them out of reach of NO_IDENTITY.
  const identity = impersonatedUserId ? `imp:${impersonatedUserId}` : uid ? `fb:${uid}` : undefined

  return {
    key: `${LocalStorageKey.REGION_ID}:${identity ?? NO_IDENTITY}`,
    hasIdentity: !!identity,
  }
}
